
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  category: string;
  image: string;
  readTime: string;
  tags: string[];
  slug?: string;
}

export default function Blog() {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const blogPosts: BlogPost[] = [
    {
      id: '1',
      title: 'Kako da zaštitite svoje parking mesto od neovlašćenog parkiranja',
      excerpt: 'Otkrijte najefektivnije načine da sprečite neovlašćeno parkiranje na vašem privatnom parking mestu.',
      content: 'Neovlašćeno parkiranje je jedan od najčešćih problema sa kojima se suočavaju vlasnici privatnih parking mesta...',
      author: 'Marko Petrović',
      date: '2025-01-15',
      category: 'saveti',
      image: 'https://readdy.ai/api/search-image?query=Private%20parking%20space%20protection%20smart%20barrier%20system%20modern%20urban%20setting%20professional%20photography%20clean%20background&width=600&height=400&seq=blog1&orientation=landscape',
      readTime: '5 min',
      tags: ['parking', 'zaštita', 'barijera']
    },
    {
      id: '2',
      title: 'Instalacija parking barijere - korak po korak vodič',
      excerpt: 'Detaljno uputstvo za instalaciju vaše nove ParkGuard barijere za manje od 30 minuta.',
      content: 'Instalacija parking barijere nikad nije bila lakša. Sledite naš detaljni vodič...',
      author: 'Ana Jovanović',
      date: '2025-01-12',
      category: 'instalacija',
      image: 'https://readdy.ai/api/search-image?query=Installation%20process%20parking%20barrier%20tools%20equipment%20step%20by%20step%20guide%20professional%20photography%20modern%20design%20clean%20background&width=600&height=400&seq=blog2&orientation=landscape',
      readTime: '8 min',
      tags: ['instalacija', 'vodič', 'alati']
    },
    {
      id: '3',
      title: 'Prednosti baterijskih vs žičnih parking barijera',
      excerpt: 'Uporedite karakteristike i prednosti različitih tipova parking barijera da biste doneli pravu odluku.',
      content: 'Kada birate parking barijeru, važno je razumeti razlike između baterijskih i žičnih modela...',
      author: 'Stefan Nikolić',
      date: '2025-01-10',
      category: 'proizvodi',
      image: 'https://readdy.ai/api/search-image?query=Comparison%20battery%20powered%20vs%20wired%20parking%20barriers%20modern%20technology%20professional%20photography%20clean%20background%20urban%20setting&width=600&height=400&seq=blog3&orientation=landscape',
      readTime: '6 min',
      tags: ['baterijski', 'žični', 'poređenje']
    },
    {
      id: '4',
      title: 'Održavanje parking barijere - saveti za dugovečnost',
      excerpt: 'Jednostavni saveti za održavanje koji će produžiti životni vek vaše parking barijere.',
      content: 'Pravilno održavanje je ključ za dugotrajno funkcionisanje vaše parking barijere...',
      author: 'Milica Stojanović',
      date: '2025-01-08',
      category: 'održavanje',
      image: 'https://readdy.ai/api/search-image?query=Parking%20barrier%20maintenance%20cleaning%20care%20professional%20photography%20modern%20equipment%20clean%20background%20urban%20setting&width=600&height=400&seq=blog4&orientation=landscape',
      readTime: '4 min',
      tags: ['održavanje', 'čišćenje', 'dugovečnost']
    },
    {
      id: '5',
      title: 'Pravni aspekti privatnog parkiranja u Srbiji',
      excerpt: 'Šta zakon kaže o privatnim parking mestima i kako da se zaštitite od prekršaja.',
      content: 'Poznavanje pravnih aspekata privatnog parkiranja je važno za sve vlasnice nekretnina...',
      author: 'Aleksandar Mitrović',
      date: '2025-01-05',
      category: 'pravo',
      image: 'https://readdy.ai/api/search-image?query=Legal%20aspects%20private%20parking%20law%20documents%20professional%20photography%20modern%20office%20setting%20clean%20background&width=600&height=400&seq=blog5&orientation=landscape',
      readTime: '7 min',
      tags: ['pravo', 'zakon', 'privatno parkiranje']
    },
    {
      id: '6',
      title: 'Tehnološke inovacije u parking sistemima',
      excerpt: 'Kako pametne tehnologije menjaju način na koji upravljamo parking prostorima.',
      content: 'Moderna tehnologija donosi revoluciju u upravljanju parking prostorima...',
      author: 'Nikola Đorđević',
      date: '2025-01-03',
      category: 'tehnologija',
      image: 'https://readdy.ai/api/search-image?query=Smart%20parking%20technology%20innovation%20modern%20systems%20IoT%20sensors%20professional%20photography%20futuristic%20design%20clean%20background&width=600&height=400&seq=blog6&orientation=landscape',
      readTime: '9 min',
      tags: ['tehnologija', 'inovacije', 'pametni sistemi']
    }
  ];

  const categories = [
    { id: 'all', name: 'Sve kategorije', count: blogPosts.length },
    { id: 'saveti', name: 'Saveti', count: blogPosts.filter(post => post.category === 'saveti').length },
    { id: 'instalacija', name: 'Instalacija', count: blogPosts.filter(post => post.category === 'instalacija').length },
    { id: 'proizvodi', name: 'Proizvodi', count: blogPosts.filter(post => post.category === 'proizvodi').length },
    { id: 'održavanje', name: 'Održavanje', count: blogPosts.filter(post => post.category === 'održavanje').length },
    { id: 'pravo', name: 'Pravo', count: blogPosts.filter(post => post.category === 'pravo').length },
    { id: 'tehnologija', name: 'Tehnologija', count: blogPosts.filter(post => post.category === 'tehnologija').length }
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sr-RS', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center cursor-pointer" onClick={scrollToTop}>
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                <i className="ri-parking-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">ParkGuard</span>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="/" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                Početna
              </a>
              <a href="/#demo" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                Proizvod
              </a>
              <a href="/#features" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                Karakteristike
              </a>
              <a href="/blog" className="text-blue-600 font-medium">
                Blog
              </a>
            </nav>
            <div className="hidden md:flex items-center space-x-4">
              <a
                href="/#checkout-section"
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                Kupi sada
              </a>
            </div>
            <button className="md:hidden w-10 h-10 flex items-center justify-center">
              <i className="ri-menu-line text-xl text-gray-600"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-6 py-3 rounded-full text-sm font-semibold mb-8 shadow-lg">
            <i className="ri-article-line mr-2"></i>
            <span>Blog i saveti</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
            ParkGuard <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Blog</span>
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12">
            Otkrijte korisne savete, vodiče i najnovije vesti o parking sistemima i zaštiti privatnih parking mesta
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-2xl blur-xl"></div>
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-2">
                <div className="flex items-center">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-search-line text-white text-xl"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Pretražite blog postove..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 bg-transparent text-white placeholder-gray-300 focus:outline-none text-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-8">
                {/* Categories */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-6">Kategorije</h3>
                  <div className="space-y-2">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all cursor-pointer ${
                          selectedCategory === category.id
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                        }`}
                      >
                        <span className="font-medium">{category.name}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          selectedCategory === category.id
                            ? 'bg-white/20 text-white'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {category.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Popular Tags */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-6">Popularne teme</h3>
                  <div className="flex flex-wrap gap-2">
                    {['parking', 'barijera', 'instalacija', 'održavanje', 'saveti', 'tehnologija', 'zaštita', 'pravo'].map((tag) => (
                      <button
                        key={tag}
                        onClick={() => setSearchTerm(tag)}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-blue-100 hover:text-blue-600 transition-colors cursor-pointer"
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Newsletter */}
                <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
                  <h3 className="text-lg font-bold mb-4">Budite u toku</h3>
                  <p className="text-blue-100 mb-6 text-sm">
                    Prijavite se za naš newsletter i budite prvi koji će saznati o novim blog postovima i savjetima.
                  </p>
                  <form className="space-y-3">
                    <input
                      type="email"
                      placeholder="Vaš email"
                      className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                    />
                    <button
                      type="submit"
                      className="w-full bg-white text-blue-600 font-bold py-3 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      Prijavite se
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* Blog Posts */}
            <div className="lg:col-span-3">
              {filteredPosts.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-search-line text-gray-400 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Nema rezultata</h3>
                  <p className="text-gray-600 mb-6">
                    Pokušajte sa drugim pojmom pretrage ili izaberite drugu kategoriju.
                  </p>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                    }}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Resetuj filtere
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {filteredPosts.map((post) => (
                    <article key={post.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group">
                      <div className="relative overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                            {categories.find(cat => cat.id === post.category)?.name}
                          </span>
                        </div>
                        <div className="absolute top-4 right-4">
                          <span className="bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs">
                            {post.readTime}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                          {post.title}
                        </h2>

                        <p className="text-gray-600 mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>

                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 text-sm">{post.date}</span>
                          <a
                            href={post.slug}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                          >
                            <span>Pročitaj više</span>
                          </a>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {/* Load More Button */}
              {filteredPosts.length > 0 && (
                <div className="text-center mt-16">
                  <button className="bg-white border border-gray-300 text-gray-700 px-8 py-4 rounded-xl hover:bg-gray-50 transition-colors font-medium">
                    Učitaj više postova
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Spremni da zaštitite svoje parking mesto?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Pridružite se hiljadama zadovoljnih korisnika koji su rešili problem neovlašćenog parkiranja sa ParkGuard barijером.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#checkout-section"
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <i className="ri-shopping-cart-line mr-2"></i>
              Naručite sada
            </a>
            <a
              href="/#demo"
              className="bg-white/10 backdrop-blur-sm border border-white/20 text-white font-bold py-4 px-8 rounded-xl hover:bg-white/20 transition-all duration-300"
            >
              <i className="ri-play-circle-line mr-2"></i>
              Pogledajte demo
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-parking-line text-white text-xl"></i>
                </div>
                <span className="text-xl font-bold">ParkGuard</span>
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                Revolucionarna pametna parking barijera koja štiti vaše privatno parking mesto od neovlašćenog korišćenja.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Brzi linkovi</h3>
              <ul className="space-y-2">
                <li><a href="/" className="text-gray-400 hover:text-white transition-colors">Početna</a></li>
                <li><a href="/#demo" className="text-gray-400 hover:text-white transition-colors">Proizvod</a></li>
                <li><a href="/#features" className="text-gray-400 hover:text-white transition-colors">Karakteristike</a></li>
                <li><a href="/blog" className="text-gray-400 hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Podrška</h3>
              <ul className="space-y-2">
                <li><a href="/#faq" className="text-gray-400 hover:text-white transition-colors">FAQ</a></li>
                <li><a href="/garancija" className="text-gray-400 hover:text-white transition-colors">Garancija</a></li>
                <li><a href="/povracaj" className="text-gray-400 hover:text-white transition-colors">Povraćaj</a></li>
                <li><a href="tel:+381612929053" className="text-gray-400 hover:text-white transition-colors">Kontakt</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">© 2025 ParkGuard. Sva prava zadržana.</p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Uslovi korišćenja</a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Privatnost</a>
              <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-white text-sm transition-colors">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
